import { Globe } from "lucide-react";
import { cn } from "@/lib/cn";

/**
 * RegionFlag — the single source of truth for rendering a market/region flag.
 *
 * Flags are rendered as SVG IMAGES, never emoji. Windows' Segoe UI Emoji
 * deliberately omits country-flag glyphs, so Chrome/Edge on Windows render the
 * bare 2-letter code (e.g. boxed "GB") instead of a flag — only macOS / Firefox
 * show emoji flags. SVG bypasses the system font, so every OS/browser shows a
 * real flag. Use this component everywhere a market flag is needed; do NOT
 * reintroduce emoji-flag helpers (that's what created the Windows bug).
 *
 * Canonical flag SVGs (lipis/flag-icons) via jsdelivr — ~1KB each, edge-cached.
 */

/** Region/market code → ISO-3166-1-alpha-2 (lowercase) for the flag SVG.
 *  Most market codes ARE the ISO code; `UK` is our internal label for `gb`,
 *  `EU` has its own flag. `INT`/`ALL`/unknown → null (render a globe). */
function regionToIso(region: string | null | undefined): string | null {
  if (!region) return null;
  const r = region.trim().toUpperCase();
  if (r === "UK") return "gb";
  if (r === "EU") return "eu";
  if (r === "INT" || r === "ALL" || r === "GLOBAL") return null;
  if (!/^[A-Z]{2}$/.test(r)) return null;
  return r.toLowerCase();
}

const flagUrl = (iso: string) =>
  `https://cdn.jsdelivr.net/gh/lipis/flag-icons@7/flags/4x3/${iso}.svg`;

export function RegionFlag({
  region,
  size = 18,
  className,
}: {
  region: string | null | undefined;
  /** Width in px; height is derived from the 4x3 aspect. */
  size?: number;
  className?: string;
}) {
  const iso = regionToIso(region);
  if (!iso) {
    return (
      <Globe
        size={Math.round(size * 0.78)}
        strokeWidth={2}
        className={cn("text-text-muted shrink-0", className)}
      />
    );
  }
  return (
    <img
      src={flagUrl(iso)}
      alt={region ?? ""}
      width={size}
      height={Math.round((size * 3) / 4)}
      loading="lazy"
      className={cn("rounded-[2px] object-cover shrink-0 ring-1 ring-black/5", className)}
    />
  );
}
